import { redirect } from '@tanstack/react-router';

import { getCookie, setCookie } from 'shared/client/utils/cookies';
import {
  LOGIN_PATH,
  LOBBY_PATH,
  PLAYER_ASSIGNMENT_PLENARY_PATH,
  PLAYER_PATH,
  PLAYER_PLENARY_PATH,
  STUDIO_PATH,
  TRANSLATIONS_PATH,
  UNAUTHORIZED_PATH
} from 'shared/common/consts/paths';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from 'shared/common/consts/supportedLanguages';
import { UI_LANGUAGE_COOKIE } from 'shared/utils/consts';
import { getEnvVar } from 'shared/utils/getEnvVar';
import { getPlenaryActivityInformation } from 'shared/utils/plenary/getPlenaryActivityInformation';
import { isAbsoluteURL, verifyHostName } from 'shared/utils/url.utils';

function isPublicPath(path: string): boolean {
  const PUBLIC_PATHS = [LOGIN_PATH, LOBBY_PATH];
  return PUBLIC_PATHS.some(p => path.startsWith(p));
}

export function isValidRedirect(redirectURL: string, whitelistDomains: string[] | null): boolean {
  if (whitelistDomains?.length && isAbsoluteURL(redirectURL)) {
    return verifyHostName(redirectURL, whitelistDomains);
  }

  if (/^[\w-]+\.[\w.-]+(?:\/|$)/.test(redirectURL)) {
    return false;
  }

  return true;
}

function getAllowedDomains(): string[] | null {
  const allowedDomainsEnv = getEnvVar('VITE_ALLOWED_REDIRECT_DOMAINS', { throwOnMissing: false });
  if (allowedDomainsEnv) {
    return allowedDomainsEnv.split(',').map(d => d.trim());
  }
  return null;
}

function verifyPlenaryCookieClient(activityInfo: ReturnType<typeof getPlenaryActivityInformation>): boolean {
  if (!activityInfo.id) {
    return false;
  }

  const cookieName = `plenary_student_${activityInfo.type}_${activityInfo.id}`;
  return getCookie(cookieName) !== null;
}

export function handleLanguageFromQuery(searchParams: URLSearchParams) {
  const language = searchParams.get('lang');
  if (language) {
    const finalLanguage = (SUPPORTED_LANGUAGES as readonly string[]).includes(language) ? language : DEFAULT_LANGUAGE;
    setCookie(UI_LANGUAGE_COOKIE, finalLanguage);
  }
}

export function handleCourseIdFromQuery(searchParams: URLSearchParams) {
  const courseDraftId = searchParams.get('courseDraftId');
  if (courseDraftId) {
    sessionStorage.setItem('x-course-id', courseDraftId);
  }
}

export function clientMiddleware({
  auth,
  location,
  search
}: {
  auth: { isUser: boolean; isAdmin: boolean | null };
  location: { pathname: string; search: string };
  search: Record<string, unknown>;
}): void | never {
  const path = location.pathname;
  const searchParams = new URLSearchParams(location.search);

  handleLanguageFromQuery(searchParams);
  handleCourseIdFromQuery(searchParams);

  const isStudioPath = path.startsWith(STUDIO_PATH);
  const isPlayerPath = path.startsWith(PLAYER_PATH);
  const isLoginPath = path.startsWith(LOGIN_PATH);
  const isUnauthorizedPath = path.startsWith(UNAUTHORIZED_PATH);
  const isTranslationsPath = path.startsWith(TRANSLATIONS_PATH);
  const isPlayerAsPreview = isPlayerPath && (searchParams.get('mode') === 'preview' || search.mode === 'preview');
  const isPlenaryRoute = isPlayerPath && path.includes('/plenary');
  const isLogoutPath = path.startsWith('/logout');
  const isRedirectPath = path.startsWith('/redirect');

  if (isPlayerAsPreview && !auth.isAdmin) {
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.delete('mode');
    throw redirect({
      to: path,
      search: Object.fromEntries(newSearchParams)
    });
  }

  const redirectTo = searchParams.get('redirectTo') || (search.redirectTo as string | undefined);
  if (redirectTo && !isValidRedirect(redirectTo, getAllowedDomains())) {
    const newSearch = { ...search };
    delete newSearch.redirectTo;
    throw redirect({
      to: '/',
      search: newSearch
    });
  }

  if ((isLogoutPath || isRedirectPath) && !auth.isUser && redirectTo && !isPublicPath(redirectTo)) {
    throw redirect({
      to: LOGIN_PATH,
      search: { redirectTo }
    });
  }

  if (isStudioPath) {
    if (!auth.isUser) {
      throw redirect({
        to: LOGIN_PATH,
        search: {
          redirectTo: `${path}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
        }
      });
    }

    if (!auth.isAdmin) {
      throw redirect({ to: UNAUTHORIZED_PATH });
    }

    return;
  }

  if (!isStudioPath) {
    if (isLoginPath && auth.isUser) {
      throw redirect({
        to: (searchParams.get('redirectTo') as string) || STUDIO_PATH
      });
    }

    if (isUnauthorizedPath && !auth.isUser) {
      throw redirect({
        to: LOGIN_PATH,
        search: { redirectTo: searchParams.get('redirectTo') as string }
      });
    }

    if (isPlenaryRoute) {
      return;
    }

    if (isPlayerPath && !auth.isUser) {
      const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
      if (url) {
        const activityInfo = getPlenaryActivityInformation(url);
        const doesHavePlenaryToken = verifyPlenaryCookieClient(activityInfo);
        if (doesHavePlenaryToken) {
          if (activityInfo.type === 'regular') {
            throw redirect({
              to: `${PLAYER_PLENARY_PATH}/$learningObjectDraftId`,
              params: { learningObjectDraftId: activityInfo.id },
              search: Object.fromEntries(searchParams) as { page?: string; studentId?: string }
            });
          } else if (activityInfo.type === 'assignment') {
            throw redirect({
              to: `${PLAYER_ASSIGNMENT_PLENARY_PATH}/$assignmentId`,
              params: { assignmentId: activityInfo.id },
              search: Object.fromEntries(searchParams) as { page?: string; studentId?: string }
            });
          }
        }
      }
      const redirectSearch = searchParams.toString() ? `?${searchParams.toString()}` : '';
      throw redirect({
        to: LOGIN_PATH,
        search: {
          redirectTo: `${path}${redirectSearch}`
        }
      });
    }

    if (isTranslationsPath && !auth.isAdmin) {
      throw redirect({
        to: LOGIN_PATH,
        search: { redirectTo: path }
      });
    }

    return;
  }
}
